/**
 * Solana NFT Minting Utilities
 * Supports both client-side (user wallet) and server-side (dev wallet) minting
 */

import { Connection, Keypair, PublicKey, clusterApiUrl, TransactionMessage, VersionedTransaction } from '@solana/web3.js';
import { Metaplex, keypairIdentity, walletAdapterIdentity } from '@metaplex-foundation/js';
import { supabase } from '../lib/supabase';
import { listPieceForSale } from './marketplaceTransactions';
import { sendAndIdempotentlyConfirm, isDuplicateSuccess } from './tx/sendIdempotent';
import { PLATFORM_WALLET_PUBKEY } from '../config/platform';
import { detectTokenProgram, deriveAta, buildApproveDelegateIx, isProgrammableNft } from './tokenHelpers';
import { trackMintActivity } from './mintActivityTracking';
import { getRpcEndpoint } from '../config/rpc';
import { queueTransaction } from './transactionQueue';

/**
 * Get Solana cluster configuration
 * @returns {Object} Cluster configuration object
 */
export function getClusterConfig() {
  const cluster = import.meta.env.VITE_SOLANA_CLUSTER || 'devnet';
  
  // Use central RPC resolver - client never uses Helius
  const rpcUrl = getRpcEndpoint({ preferHelius: false });
  
  const configs = {
    'devnet': {
      cluster: 'devnet',
      rpcUrl: rpcUrl,
      explorerUrl: 'https://explorer.solana.com/?cluster=devnet',
      isMainnet: false
    },
    'mainnet-beta': {
      cluster: 'mainnet-beta',
      rpcUrl: rpcUrl,
      explorerUrl: 'https://explorer.solana.com/?cluster=mainnet-beta',
      isMainnet: true
    },
    'testnet': {
      cluster: 'testnet',
      rpcUrl: rpcUrl,
      explorerUrl: 'https://explorer.solana.com/?cluster=testnet',
      isMainnet: false
    }
  };

  return configs[cluster] || configs['devnet'];
}

/**
 * Check if we're on mainnet
 * @returns {boolean} True if on mainnet
 */
export function isMainnet() {
  return getClusterConfig().isMainnet;
}

/**
 * Get appropriate RPC endpoints for the current cluster
 * @returns {Array} Array of RPC endpoint URLs
 */
export function getRPCEndpoints() {
  const config = getClusterConfig();
  
  // Use central RPC resolver - now uses Helius for mainnet reliability
  const primaryRpc = getRpcEndpoint({ preferHelius: false });
  
  if (config.isMainnet) {
    // For mainnet, prioritize Helius if available, then fallback to public RPCs
    const heliusKey = import.meta.env.VITE_HELIUS_API_KEY;
    const endpoints = [];
    
    // Add Helius first if available (most reliable)
    if (heliusKey) {
      endpoints.push(`https://mainnet.helius-rpc.com/?api-key=${heliusKey}`);
    }
    
    // Add public fallbacks (may be rate-limited)
    endpoints.push(
      'https://api.mainnet-beta.solana.com',
      'https://rpc.ankr.com/solana'
    );
    
    return endpoints;
  } else {
    return [
      primaryRpc,
      'https://rpc.ankr.com/solana_devnet',
      'https://solana-devnet.g.alchemy.com/v2/demo'
    ];
  }
}

/**
 * Create metadata JSON and upload to Supabase with validation
 * @param {Object} pieceData - Piece data from form
 * @returns {Object} { metadataUri, validation } - Metadata URI and validation results
 */
export async function createAndUploadMetadata(pieceData) {
  const {
    name,
    description,
    image, // Supabase public URL
    animation_url, // GLB public URL
    creator,
    attributes = [],
    sellerFeeBps = 0, // Default 0% royalty
    category
  } = pieceData;

  // Import metadata utilities (dynamic import for TypeScript)
  const { generateAndUploadMetadata } = await import('./metadata');

  try {
    // Use the robust metadata generation pipeline
    const result = await generateAndUploadMetadata({
      name,
      description,
      imageUrl: image,
      modelUrl: animation_url,
      royaltyBps: sellerFeeBps,
      creator,
      category,
      attributes,
      externalUrl: 'https://outsera.io',
    });

    console.log('✅ Metadata generated and validated:', {
      uri: result.metadataUri,
      imageSize: result.validation.image.contentLength,
      modelSize: result.validation.model.contentLength,
    });

    return result;
  } catch (error) {
    console.error('❌ Metadata generation failed:', error);
    throw new Error(`Failed to create metadata: ${error.message}`);
  }
}

/**
 * Mint NFT using connected wallet WITH QUEUE PROTECTION
 * Queues mints to prevent RPC rate limit issues with concurrent users
 * @param {Object} walletAdapter - Solana wallet adapter instance
 * @param {Object} pieceData - Piece metadata
 * @param {Object} options - Queue options
 * @returns {Object} { mintAddress, signature }
 */
export async function mintWithUserWallet(walletAdapter, pieceData, options = {}) {
  const {
    useQueue = true, // Set to false to bypass queue (not recommended)
    priority = 1,
    onProgress = null
  } = options;

  // If queue is disabled, call internal function directly
  if (!useQueue) {
    return await mintWithUserWalletInternal(walletAdapter, pieceData);
  }

  // Queue the mint operation to prevent RPC rate limit issues
  // Max 15 concurrent mints to stay within Helius limits
  console.log(`🎨 Queueing mint operation for: ${pieceData.name}`);
  
  return await queueTransaction(
    () => mintWithUserWalletInternal(walletAdapter, pieceData),
    {
      operationType: 'mint',
      maxConcurrencyForType: 15, // Max 15 concurrent mints (adjustable based on Helius tier)
      priority,
      timeout: 180000, // 3 minutes timeout for minting
      retries: 3, // Retry up to 3 times on failure
      id: `mint-${pieceData.name}-${Date.now()}`,
      onProgress
    }
  );
}

/**
 * Internal mint implementation (wrapped by queue)
 * DO NOT CALL DIRECTLY - Use mintWithUserWallet instead
 * @param {Object} walletAdapter - Solana wallet adapter instance
 * @param {Object} pieceData - Piece metadata
 * @returns {Object} { mintAddress, signature }
 */
async function mintWithUserWalletInternal(walletAdapter, pieceData) {
  console.log('🔍 mintWithUserWallet - Wallet check:');
  console.log('  - walletAdapter:', walletAdapter);
  console.log('  - connected:', walletAdapter.connected);
  console.log('  - isConnected:', walletAdapter.isConnected);
  console.log('  - publicKey:', walletAdapter.publicKey?.toString());
  
  // Check both 'connected' (standard) and 'isConnected' (Phantom) properties
  const isWalletConnected = walletAdapter.connected || walletAdapter.isConnected;
  
  if (!isWalletConnected || !walletAdapter.publicKey) {
    console.error('❌ Wallet not connected - connected:', walletAdapter.connected, 'isConnected:', walletAdapter.isConnected, 'publicKey:', walletAdapter.publicKey);
    throw new Error('Wallet not connected');
  }
  
  console.log('✅ Wallet verification passed - proceeding with mint');

  // Try multiple RPC endpoints for better reliability
  const RPC_ENDPOINTS = getRPCEndpoints();
  
  let connection = null;
  let workingRPC = null;
  
  // Find a working RPC
  for (const rpc of RPC_ENDPOINTS) {
    try {
      const testConnection = new Connection(rpc, {
        commitment: 'confirmed',
        confirmTransactionInitialTimeout: 60000
      });
      // Test if RPC works
      await testConnection.getLatestBlockhash();
      connection = testConnection;
      workingRPC = rpc;
      break;
    } catch (error) {
      console.warn(`RPC ${rpc} failed, trying next...`);
      continue;
    }
  }
  
  if (!connection) {
    throw new Error('All RPC endpoints failed. Please try again later.');
  }
  
  // Configure Metaplex with proper transaction handling for Phantom
  // Ensure wallet identity is properly configured
  const mx = Metaplex
    .make(connection)
    .use(walletAdapterIdentity(walletAdapter));
  
  // Log app identity for debugging
  console.log('🆔 App Identity:', {
    name: import.meta.env.VITE_APP_NAME || 'Outsera',
    url: import.meta.env.VITE_APP_URL || 'https://outsera.io',
    hasIcon: !!(import.meta.env.VITE_APP_ICON)
  });

  const clusterConfig = getClusterConfig();
  console.log('🔗 Minting with user wallet:', walletAdapter.publicKey.toString());
  console.log('🌐 Using RPC:', workingRPC);
  console.log('🏗️ Cluster:', clusterConfig.cluster);
  console.log('🔐 Wallet adapter type:', walletAdapter.name || 'Unknown');
  
  // Log wallet identity for debugging Phantom trust issues
  if (walletAdapter.name === 'Phantom') {
    console.log('👻 Phantom wallet detected - ensuring proper transaction flow');
  }
  
  // Warning for mainnet operations
  if (isMainnet()) {
    console.warn('⚠️ WARNING: You are minting on MAINNET! This will cost real SOL.');
  }

  // Check balance first
  try {
    const balance = await connection.getBalance(walletAdapter.publicKey);
    const solBalance = balance / 1e9;
    console.log('💰 Wallet balance:', solBalance, 'SOL');
    
    if (balance < 10000000) { // Less than 0.01 SOL
      throw new Error(`Insufficient balance: ${solBalance} SOL. Need at least 0.01 SOL for minting. Get devnet SOL at https://solfaucet.com/`);
    }
  } catch (error) {
    if (error.message.includes('Insufficient balance')) {
      throw error;
    }
    console.warn('Could not check balance:', error.message);
  }

  // Create metadata URI with validation
  const metadataResult = await createAndUploadMetadata({
    ...pieceData,
    creator: walletAdapter.publicKey.toString()
  });

  console.log('📄 Metadata URI:', metadataResult.metadataUri);
  console.log('📊 Asset validation:', {
    image: `${metadataResult.validation.image.contentType} (${formatBytes(metadataResult.validation.image.contentLength)})`,
    model: `${metadataResult.validation.model.contentType} (${formatBytes(metadataResult.validation.model.contentLength)})`
  });

  // Mint the NFT using idempotent sender
  let mintAddress;
  let mintSig;
  
  try {
    console.log('🎨 Building mint transaction...');
    
    // Create NFT with Phantom-compatible options
    // Using standard settings that work with Phantom's security requirements
    // Add retry logic for mainnet delays
    let createResult;
    const maxRetries = 5;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        if (attempt > 0) {
          console.log(`⏳ Retry attempt ${attempt}/${maxRetries - 1} after ${attempt * 2}s delay...`);
          await new Promise(resolve => setTimeout(resolve, attempt * 2000)); // Exponential backoff
        }
        
        createResult = await mx.nfts().create({
          uri: metadataResult.metadataUri,
          name: pieceData.name,
          sellerFeeBasisPoints: pieceData.sellerFeeBps || 500, // 5% royalty
          symbol: 'M3D',
          isMutable: true,
          tokenOwner: walletAdapter.publicKey, // Explicitly set token owner
          creators: [
            {
              address: walletAdapter.publicKey,
              share: 100,
              verified: true
            }
          ]
        });
        
        // If successful, break out of retry loop
        console.log('✅ NFT creation successful on attempt', attempt + 1);
        break;
        
      } catch (retryError) {
        const errorMsg = retryError.message || String(retryError);
        
        // Check if it's an account not found error (mainnet propagation delay)
        if (errorMsg.includes('MintAccount') && errorMsg.includes('not found')) {
          console.warn(`⚠️ Mint account not found yet (attempt ${attempt + 1}/${maxRetries})`);
          
          // If this is the last retry, throw the error
          if (attempt === maxRetries - 1) {
            throw new Error('NFT minted but account verification timed out. Please check your wallet - the NFT may have been created.');
          }
          // Otherwise, continue to next retry
          continue;
        }
        
        // For other errors, throw immediately
        throw retryError;
      }
    }

    // Guard against undefined response
    if (!createResult || !createResult.nft) {
      throw new Error('Mint create returned no NFT result');
    }

    const { nft } = createResult;
    mintAddress = nft.address.toBase58();
    
    // Validate mint address before proceeding
    const { ensureValidMintAddress } = await import('./mintAddressValidation.js');
    ensureValidMintAddress(mintAddress, 'NFT mint');
    
    // Guard against undefined signature
    if (!nft.response || !nft.response.signature) {
      console.warn('⚠️ NFT response missing signature, attempting to recover...');
      
      // Try to get the most recent signature from the wallet
      const connection = mx.connection;
      const recentSigs = await connection.getSignaturesForAddress(walletAdapter.publicKey, { limit: 3 });
      if (recentSigs && recentSigs.length > 0) {
        mintSig = recentSigs[0].signature;
        console.log('✅ Recovered signature from recent transactions:', mintSig);
      } else {
        throw new Error('Mint succeeded but signature could not be recovered');
      }
    } else {
      mintSig = nft.response.signature;
    }
    
    console.log('✅ NFT minted:', mintAddress);
    console.log('📝 Mint signature:', mintSig);

  } catch (mintError) {
    console.error('❌ Minting error:', mintError);
    
    // Extract logs if available
    if (mintError.__rpcLogs) {
      console.error('📋 Transaction logs:', mintError.__rpcLogs);
    }
    
    // Handle duplicate processed - try to recover the mint
    if (isDuplicateSuccess(mintError) || mintError.message?.includes('already been processed')) {
      console.log('⚠️ Duplicate transaction detected - attempting to recover mint...');
      
      try {
        // Try to find recent NFT mints from this wallet
        const connection = mx.connection;
        const recentSigs = await connection.getSignaturesForAddress(walletAdapter.publicKey, { limit: 5 });
        
        console.log(`🔍 Checking ${recentSigs.length} recent transactions...`);
        
        // Look through recent transactions to find a mint
        for (const sigInfo of recentSigs) {
          try {
            const tx = await connection.getTransaction(sigInfo.signature, {
              maxSupportedTransactionVersion: 0,
              commitment: 'confirmed'
            });
            
            if (tx?.meta?.postTokenBalances) {
              // Look for a new token account (mint = 1)
              for (const balance of tx.meta.postTokenBalances) {
                if (balance.uiTokenAmount.uiAmount === 1 && balance.uiTokenAmount.decimals === 0) {
                  // This looks like an NFT mint
                  const possibleMint = balance.mint;
                  console.log('🎨 Found potential NFT mint:', possibleMint);
                  
                  // Verify this is a valid NFT by trying to fetch it
                  try {
                    const nft = await mx.nfts().findByMint({ mintAddress: new PublicKey(possibleMint) });
                    if (nft && nft.name === pieceData.name) {
                      console.log('✅ Recovered NFT successfully!');
                      mintAddress = possibleMint;
                      mintSig = sigInfo.signature;
                      break;
                    }
                  } catch (e) {
                    // Not the right NFT, continue searching
                    continue;
                  }
                }
              }
              if (mintAddress) break;
            }
          } catch (txError) {
            // Skip this transaction
            continue;
          }
        }
        
        if (!mintAddress) {
          console.warn('⚠️ Could not automatically recover mint. Please check your wallet or try again.');
          throw new Error('Transaction may have succeeded. Please check your wallet and refresh the page. If the NFT appears in your wallet, you can skip minting.');
        }
        
        console.log('✅ Successfully recovered from duplicate transaction!');
        console.log('📝 Mint address:', mintAddress);
        console.log('📝 Mint signature:', mintSig);
        
      } catch (recoveryError) {
        console.error('❌ Recovery failed:', recoveryError);
        throw new Error('Transaction already processed. Please refresh to check if mint succeeded, or try again in a few moments.');
      }
    } else {
      // Better error messages for other errors
      if (mintError.message?.includes('blockhash not found')) {
        throw new Error('Network congestion detected. Please wait 30 seconds and try again.');
      }
      if (mintError.message?.includes('insufficient funds')) {
        throw new Error('Insufficient SOL for minting. Get devnet SOL at https://solfaucet.com/');
      }
      if (mintError.message?.includes('failed to send transaction')) {
        throw new Error('Network error. Please check your connection and try again.');
      }
      
      throw new Error(`Minting failed: ${mintError.message || 'Unknown error'}`);
    }
  }

  // Step 2: Immediately request delegate approval (seller signs now)
  let approveSig;
  let delegateActive = false;
  let sellerAta;
  let platformDelegate;
  let tokenProgram;
  let expiresAt;
  
  try {
    console.log('🔐 Requesting delegate approval for future sales...');
    
    const mintPk = new PublicKey(mintAddress);
    platformDelegate = new PublicKey(PLATFORM_WALLET_PUBKEY);
    
    // Detect token program (SPL Token vs Token-2022)
    tokenProgram = await detectTokenProgram(connection, mintPk);
    console.log('🔍 Token program detected:', tokenProgram);
    
    // Check if programmable NFT (should not be for standard mints)
    const isPNFT = await isProgrammableNft(connection, mintPk);
    if (isPNFT) {
      console.warn('⚠️ Programmable NFT detected - delegate approval skipped');
      throw new Error('This is a programmable NFT. Delegate approval not supported.');
    }
    
    // Derive seller's ATA
    sellerAta = deriveAta(walletAdapter.publicKey, mintPk, tokenProgram);
    console.log('📍 Seller ATA:', sellerAta.toBase58());
    
    // Build approve instruction
    const approveIx = buildApproveDelegateIx({
      ata: sellerAta,
      owner: walletAdapter.publicKey,
      delegate: platformDelegate,
      program: tokenProgram
    });
    
    // Build and send approve transaction
    const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
    
    const approveMsg = new TransactionMessage({
      payerKey: walletAdapter.publicKey,
      recentBlockhash: blockhash,
      instructions: [approveIx]
    }).compileToV0Message();
    
    const approveTx = new VersionedTransaction(approveMsg);
    
    // Request signature from user
    console.log('✍️ Requesting signature for delegate approval...');
    const signedApproveTx = await walletAdapter.signTransaction(approveTx);
    
    // Ensure the transaction was actually signed
    if (!signedApproveTx?.signatures?.length) {
      throw new Error('Wallet did not sign the delegate approval transaction');
    }
    
    // Send with idempotent confirmation
    const approveResult = await sendAndIdempotentlyConfirm(
      connection,
      signedApproveTx,
      walletAdapter.publicKey,
      { commitment: 'confirmed' }
    );
    
    // Defensive check - ensure we got a signature back
    if (!approveResult || !approveResult.signature) {
      throw new Error('Delegate approval returned no signature');
    }
    
    approveSig = approveResult.signature;
    
    console.log('✅ Delegate approval confirmed:', approveSig);
    
    // VERIFY the delegate was actually set on-chain
    console.log('🔍 Verifying delegate was set on-chain...');
    try {
      const { getAccount } = await import('@solana/spl-token');
      const tokenAccount = await getAccount(connection, sellerAta);
      
      if (tokenAccount.delegate && tokenAccount.delegate.toBase58() === platformDelegate.toBase58()) {
        console.log('✅ Delegate verified on-chain!');
        delegateActive = true;
      } else {
        console.warn('⚠️ Delegate not set on-chain despite transaction success');
        console.warn('   Expected:', platformDelegate.toBase58());
        console.warn('   Got:', tokenAccount.delegate?.toBase58() || 'none');
        delegateActive = false;
      }
    } catch (verifyError) {
      console.warn('⚠️ Could not verify delegate on-chain:', verifyError.message);
      delegateActive = false;
    }
    
    // Calculate expiration (7 days from now)
    expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7);
    
    // Store delegate info in database
    console.log('💾 Storing delegate authorization in database...');
    
    // Update piece by mint_address (set it if not already set, then update delegate info)
    const { data: existingPiece, error: findError } = await supabase
      .from('pieces')
      .select('id')
      .eq('mint_address', mintAddress)
      .maybeSingle();
    
    if (existingPiece) {
      // Piece already has mint_address set, just update delegate info
      const { error: updateError } = await supabase
        .from('pieces')
        .update({
          seller_ata: sellerAta.toBase58(),
          listing_delegate: platformDelegate.toBase58(),
          delegate_expires_at: expiresAt.toISOString(),
          is_delegate_active: true,
          token_program: tokenProgram,
          holder_wallet: walletAdapter.publicKey.toBase58(),
          last_error: null
        })
        .eq('mint_address', mintAddress);
      
      if (updateError) {
        console.error('Error updating delegate info:', updateError);
        throw updateError;
      }
    } else {
      // Piece doesn't have mint_address yet
      // This info will be stored when updatePieceWithMintAddress is called
      console.log('ℹ️ Piece not found by mint_address yet, delegate info will be stored later');
    }
    
    console.log('✅ Delegate authorization stored successfully');
    
  } catch (approveError) {
    console.warn('⚠️ Delegate approval failed:', approveError);
    console.warn('NFT minted successfully, but you\'ll need to approve delegate when listing');
    
    // Extract logs if available
    if (approveError.__rpcLogs) {
      console.error('📋 Approval logs:', approveError.__rpcLogs);
    }
    
    // Don't fail the entire mint - just warn
    delegateActive = false;
  }

  // Track mint activity in the database
  try {
    console.log('📝 Tracking mint activity...');
    const activityResult = await trackMintActivity({
      mintAddress,
      toWallet: walletAdapter.publicKey.toString(),
      signature: mintSig,
      network: clusterConfig.cluster // 'devnet' or 'mainnet-beta'
    });
    
    if (activityResult.success) {
      console.log('✅ Mint activity tracked successfully');
    } else {
      console.warn('⚠️ Failed to track mint activity:', activityResult.error);
      // Don't fail the mint if activity tracking fails
    }
  } catch (activityError) {
    console.warn('⚠️ Exception tracking mint activity:', activityError);
    // Don't fail the mint if activity tracking fails
  }

  return {
    mintAddress,
    mintSignature: mintSig,
    approveSignature: approveSig,
    metadataUri: metadataResult.metadataUri,
    name: pieceData.name,
    symbol: 'M3D',
    cluster: clusterConfig.cluster,
    explorerUrl: `${clusterConfig.explorerUrl}/address/${mintAddress}`,
    validation: metadataResult.validation,
    delegateActive: delegateActive,
    delegateApprovalRequired: !delegateActive,
    // Include delegate info for database storage
    delegateInfo: delegateActive ? {
      seller_ata: sellerAta.toBase58(),
      listing_delegate: platformDelegate.toBase58(),
      delegate_expires_at: expiresAt.toISOString(),
      is_delegate_active: true,
      token_program: tokenProgram
    } : null
  };
}

/**
 * Mint NFT using server wallet (for backend/admin use)
 * This requires calling a Supabase Edge Function
 * @param {Object} pieceData - Piece metadata
 * @returns {Object} { mintAddress }
 */
export async function mintWithServerWallet(pieceData) {
  // Call Supabase Edge Function
  const { data, error } = await supabase.functions.invoke('mint-nft-devnet', {
    body: {
      name: pieceData.name,
      description: pieceData.description,
      image: pieceData.image,
      animation_url: pieceData.animation_url,
      category: pieceData.category,
      attributes: pieceData.attributes || [],
      sellerFeeBps: pieceData.sellerFeeBps || 500
    }
  });

  if (error) {
    throw new Error(`Minting failed: ${error.message}`);
  }

  return data;
}

/**
 * Update piece record with mint address in Supabase and list for sale
 * @param {string} pieceId - Piece ID
 * @param {string} mintAddress - NFT mint address
 * @param {string} chain - Blockchain network (default: solana-mainnet)
 * @param {Object} delegateInfo - Delegate information from minting (optional)
 * @param {Object} wallet - Wallet adapter for creating listing (optional)
 */
export async function updatePieceWithMintAddress(pieceId, mintAddress, chain = 'solana-mainnet', delegateInfo = null, wallet = null) {
  // Validate mint address before storing in database
  const { ensureValidMintAddress } = await import('./mintAddressValidation.js');
  ensureValidMintAddress(mintAddress, 'piece update');
  
  // First get the current piece to get creator info AND check if delegate info already set
  const { data: currentPiece, error: fetchError} = await supabase
    .from('pieces')
    .select('creator_id, holder_wallet, creators!inner(wallet), price, listing_price, name, is_delegate_active, listing_delegate')
    .eq('id', pieceId)
    .single();

  if (fetchError) {
    throw new Error(`Failed to fetch piece: ${fetchError.message}`);
  }

  console.log('🔍 Current piece data before minting:', {
    id: pieceId,
    name: currentPiece.name,
    price: currentPiece.price,
    listing_price: currentPiece.listing_price,
  });

  const listingPrice = currentPiece.listing_price || currentPiece.price || 0;

  if (!listingPrice || listingPrice <= 0) {
    console.warn('⚠️ Warning: Piece has no price set. Will not auto-list.');
  }

  // Log state transition for mint
  console.log('📊 State transition [MINT]:', {
    mint_address: mintAddress,
    prev_status: 'draft',
    next_status: listingPrice > 0 ? 'listed' : 'unlisted',
    owner: currentPiece.creators.wallet,
    action: listingPrice > 0 ? 'piece_minted_auto_listed' : 'piece_minted',
    piece_id: pieceId,
    piece_name: currentPiece.name,
    listing_price: listingPrice,
  });

  // Build update object with mint address and optional delegate info
  // IMPORTANT: Only set listing_status to 'listed' if delegate is active
  // This prevents "stale listing" issues where piece is marked listed but has no listing entry
  const shouldAutoList = listingPrice > 0 && delegateInfo?.is_delegate_active;
  
  console.log('🔍 Auto-list decision:', {
    listingPrice,
    hasPrice: listingPrice > 0,
    delegateInfo: delegateInfo,
    isDelegateActive: delegateInfo?.is_delegate_active,
    shouldAutoList
  });
  
  const updateData = {
    mint_address: mintAddress,
    chain: chain,
    minted_at: new Date().toISOString(),
    // When minted, the holder becomes the creator (they own the NFT)
    holder_wallet: currentPiece.creators.wallet,
    // Only mark as 'listed' if we have active delegate (so we can create listing entry)
    // Otherwise leave as 'unlisted' and user can manually list later
    listing_status: shouldAutoList ? 'listed' : 'unlisted',
    listed_at: shouldAutoList ? new Date().toISOString() : null,
    // Set BOTH price and listing_price to ensure UI displays correctly
    price: listingPrice,
    listing_price: listingPrice,
    // CRITICAL: Transition from draft to minted/public
    // This satisfies the constraint: pieces_must_be_draft_or_minted
    is_draft: false,  // No longer a draft once minted
    is_public: true   // Minted pieces are publicly visible
  };

  // Add delegate info if provided OR if already set
  if (delegateInfo) {
    updateData.seller_ata = delegateInfo.seller_ata;
    updateData.listing_delegate = delegateInfo.listing_delegate;
    updateData.delegate_expires_at = delegateInfo.delegate_expires_at;
    updateData.is_delegate_active = delegateInfo.is_delegate_active;
    updateData.token_program = delegateInfo.token_program;
    console.log('💾 Saving delegate authorization to database...');
  } else if (currentPiece.is_delegate_active && currentPiece.listing_delegate) {
    // Delegate was already set during minting, preserve it
    console.log('ℹ️ Delegate authorization already set, preserving...');
  }

  const { data, error } = await supabase
    .from('pieces')
    .update(updateData)
    .eq('id', pieceId)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to update piece: ${error.message}`);
  }

  console.log('✅ Piece updated with mint address:', mintAddress);
  console.log('✅ Updated piece data:', {
    id: data.id,
    name: data.name,
    mint_address: data.mint_address,
    price: data.price,
    listing_price: data.listing_price,
    listing_status: data.listing_status,
    is_draft: data.is_draft,
    will_auto_list: shouldAutoList
  });

  // Create nft_activity entry for the mint event
  // This is optional - if it fails, don't break the mint process
  try {
    console.log('📝 Creating nft_activity entry for mint...');
    
    // Check if mint activity already exists for this mint address to prevent duplicates
    const { data: existingActivity, error: checkError } = await supabase
      .from('nft_activity')
      .select('id')
      .eq('mint_address', mintAddress)
      .eq('event_type', 'mint')
      .maybeSingle();

    if (checkError) {
      console.warn('⚠️ Error checking for existing mint activity:', checkError.message);
    }

    if (existingActivity) {
      console.log('ℹ️ Mint activity already exists for this mint address, skipping duplicate');
    } else {
      const now = new Date();
      const timestamp = Math.floor(now.getTime() / 1000); // Unix timestamp in seconds
      const tempSignature = 'app-mint-' + mintAddress + '-' + timestamp; // Use mint address in signature for consistency
      
      // Get the mint price from the piece data (use listing_price or price)
      const mintPrice = data.listing_price || data.price || 0;
      const mintPriceLamports = mintPrice > 0 ? Math.floor(mintPrice * 1e9) : null;
      
      // All required fields that match the schema
      const activityData = {
        mint_address: mintAddress,
        event_type: 'mint',
        to_address: currentPiece.creators.wallet, // Creator's WALLET receives the NFT (not creator_id!)
        from_address: null, // Mints don't have a from address
        event_timestamp: timestamp, // Unix timestamp in seconds
        block_time: now.toISOString(), // ISO timestamp
        created_at: now.toISOString(),
        slot: 0, // Required field - will be updated by Helius if detected
        signature: tempSignature,
        tx_sig: tempSignature,
        source: 'app', // Mark as coming from the app, not Helius
        price: mintPrice > 0 ? mintPrice : null,
        amount_lamports: mintPriceLamports
      };

      const { error: activityError } = await supabase
        .from('nft_activity')
        .insert(activityData);

      if (activityError) {
        console.warn('⚠️ Failed to create nft_activity entry (non-critical):', activityError.message);
        console.warn('Activity data attempted:', activityData);
        // Don't throw - this is optional
      } else {
        console.log('✅ nft_activity entry created successfully for mint:', mintAddress);
      }
    }
  } catch (activityError) {
    console.warn('⚠️ Activity logging failed (non-critical):', activityError);
    // Continue anyway - activity logging is optional
  }

  // CRITICAL FIX: Create listing entry in listings table if price > 0
  // This ensures the piece shows up correctly on the listings page
  // Must use edge function due to RLS policies
  if (listingPrice > 0 && delegateInfo?.is_delegate_active) {
    try {
      console.log('📝 Creating listing entry for auto-listed piece via edge function...');
      console.log('   Delegate info available:', {
        seller_ata: delegateInfo?.seller_ata,
        listing_delegate: delegateInfo?.listing_delegate,
        is_delegate_active: delegateInfo?.is_delegate_active
      });
      
      // Check if a listing already exists for this mint address
      const { data: existingListing, error: checkListingError } = await supabase
        .from('listings')
        .select('id, status')
        .eq('mint_address', mintAddress)
        .eq('status', 'active')
        .maybeSingle();

      if (checkListingError) {
        console.warn('⚠️ Error checking for existing listing:', checkListingError.message);
      }

      if (existingListing) {
        console.log('ℹ️ Listing already exists for this mint address, skipping duplicate');
      } else {
        // Call edge function to create listing (respects RLS policies)
        console.log('📤 Calling create-listing edge function...');
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-listing`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            pieceId: pieceId,
            sellerWallet: currentPiece.creators.wallet,
            mintAddress: mintAddress,
            priceSol: listingPrice,
            // Edge function will use existing delegate from pieces table
            autoList: true // Flag to indicate this is auto-listing after mint
          }),
        });

        const result = await response.json();

        if (!result.success) {
          console.error('❌ Edge function failed to create listing:', result.error);
          console.error('   Response:', result);
        } else {
          console.log('✅ Listing entry created successfully via edge function!');
          console.log('   Listing ID:', result.listing?.id);
          console.log('   Mint:', result.listing?.mint_address);
          console.log('   Price:', result.listing?.price_sol, 'SOL');
          
          // Activity is logged by the edge function, no need to log again
        }
      }
    } catch (listingCreationError) {
      console.error('❌ Listing creation exception:', listingCreationError);
      console.error('   Stack trace:', listingCreationError.stack);
      // Continue anyway - listing creation is optional but we want to see the error
    }
  } else {
    if (listingPrice > 0) {
      console.warn('⚠️ ==========================================');
      console.warn('⚠️ PIECE NOT AUTO-LISTED');
      console.warn('⚠️ ==========================================');
      console.warn('⚠️ Reason: Delegate not active');
      console.warn('⚠️ Action Required: Manually list from Listings page');
      console.warn('⚠️ Price set:', listingPrice, 'SOL');
      console.warn('⚠️ Delegate info:', delegateInfo);
      console.warn('⚠️ ==========================================');
    } else {
      console.log('ℹ️ Piece has no price set, not creating listing');
    }
  }

  console.log('✅ updatePieceWithMintAddress completed');
  console.log('   Final status:', {
    mint_address: mintAddress,
    listing_status: shouldAutoList ? 'listed' : 'unlisted',
    auto_listed: shouldAutoList
  });

  return data;
}

/**
 * Mint multiple pieces for a capsule (batch minting)
 * @param {Object} walletAdapter - Solana wallet adapter instance
 * @param {Array} pieces - Array of piece objects from database
 * @param {Function} onProgress - Callback for progress updates (pieceIndex, pieceName, status, result)
 * @returns {Promise<Object>} { successful, failed, results }
 */
export async function mintCapsulePieces(walletAdapter, pieces, onProgress = null) {
  if (!walletAdapter.connected || !walletAdapter.publicKey) {
    throw new Error('Wallet not connected');
  }

  if (!pieces || !Array.isArray(pieces) || pieces.length === 0) {
    throw new Error('No pieces provided for minting');
  }

  console.log(`🎨 Starting capsule minting for ${pieces.length} pieces...`);
  
  const results = {
    successful: [],
    failed: [],
    results: []
  };

  for (let i = 0; i < pieces.length; i++) {
    const piece = pieces[i];
    
    try {
      console.log(`\n🎨 Minting piece ${i + 1}/${pieces.length}: ${piece.name}`);
      
      // Notify progress: starting
      if (onProgress) {
        onProgress(i, piece.name, 'minting', null);
      }

      // Prepare piece data for minting
      const pieceData = {
        name: piece.name,
        description: piece.description || `${piece.name} - 3D wearable piece from Outsera`,
        image: piece.thumbnail_url || piece.image_url,
        animation_url: piece.model_url,
        category: piece.category || 'Unknown',
        attributes: piece.attributes || [],
        sellerFeeBps: piece.royalty_bps || 500, // Default 5% if not set
        creator: walletAdapter.publicKey.toString()
      };

      // Mint the piece
      const mintResult = await mintWithUserWallet(walletAdapter, pieceData);
      
      console.log(`✅ Piece ${i + 1} minted:`, mintResult.mintAddress);

      // Update database with mint address
      await updatePieceWithMintAddress(
        piece.id,
        mintResult.mintAddress,
        'solana-mainnet',
        mintResult.delegateInfo,
        walletAdapter
      );

      console.log(`✅ Piece ${i + 1} database updated`);

      // Note: Mint activity is already tracked in mintWithUserWallet function
      // No need to track again here

      // Track success
      results.successful.push(piece.id);
      results.results.push({
        pieceId: piece.id,
        pieceName: piece.name,
        success: true,
        mintAddress: mintResult.mintAddress,
        signature: mintResult.signature
      });

      // Notify progress: success
      if (onProgress) {
        onProgress(i, piece.name, 'success', {
          mintAddress: mintResult.mintAddress,
          signature: mintResult.signature
        });
      }

      // Dynamic delay between mints based on batch size
      // For large capsules (>100 pieces), use shorter delays for efficiency
      if (i < pieces.length - 1) {
        const delayMs = pieces.length > 100 ? 1000 : 2000;
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }

    } catch (error) {
      console.error(`❌ Failed to mint piece ${i + 1} (${piece.name}):`, error);
      
      // Track failure
      results.failed.push(piece.id);
      results.results.push({
        pieceId: piece.id,
        pieceName: piece.name,
        success: false,
        error: error.message
      });

      // Notify progress: error
      if (onProgress) {
        onProgress(i, piece.name, 'error', { error: error.message });
      }

      // Continue with next piece even if one fails
    }
  }

  console.log(`\n✅ Capsule minting complete!`);
  console.log(`   Successful: ${results.successful.length}`);
  console.log(`   Failed: ${results.failed.length}`);

  return results;
}

/**
 * Get Solana explorer URL for a mint address
 * @param {string} mintAddress - NFT mint address
 * @param {string} cluster - Network cluster (devnet, mainnet-beta)
 * @returns {string} Explorer URL
 */
export function getExplorerUrl(mintAddress, cluster = 'mainnet-beta') {
  return `https://explorer.solana.com/address/${mintAddress}?cluster=${cluster}`;
}

/**
 * Format bytes to human-readable string
 * @param {number} bytes - Number of bytes
 * @returns {string} Formatted string
 */
function formatBytes(bytes) {
  if (!bytes) return 'Unknown';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}
